import React, {
  useEffect,
  useCallback,
  useRef,
  useReducer,
  useImperativeHandle,
  forwardRef,
} from 'react'
import styled from '@emotion/styled'
import {
  FaChevronDown,
  FaChevronRight,
  FaSearch,
  FaChevronLeft,
} from 'react-icons/fa'
import moment from 'moment'
import { colors } from '../../../../../constants'
import { DateMenuTemplate } from '@types'
import { YSpacing } from '@components/common'
import { SearchDateMenuTemplatesReq } from '@coordinators/groupOrders/dateMenuTemplates'

// Define public ref methods that can be called by parent component
export interface TemplatesListRef {
  refreshGlobalTemplates: () => Promise<void>
  refreshMarketTemplates: () => Promise<void>
  refreshPrivateTemplates: () => Promise<void>
}

const RESULTS_PER_PAGE = 10

// Define the state interface
interface TemplatesSidebarState {
  // Templates state
  globalTemplates: DateMenuTemplate[] | null
  marketTemplates: DateMenuTemplate[] | null
  privateTemplates: DateMenuTemplate[] | null

  // Section expansion state
  globalExpanded: boolean
  marketExpanded: boolean
  privateExpanded: boolean

  // Loading state
  isLoading: boolean

  // Search state
  globalSearchTerm: string
  marketSearchTerm: string
  privateSearchTerm: string

  // Pagination state
  globalCurrentPage: number
  marketCurrentPage: number
  privateCurrentPage: number

  // Define a new state to track which section the last saved template belongs to
  lastUpdatedSection: 'global' | 'market' | 'private' | null
}

// Define action types
type TemplatesSidebarAction =
  | { type: 'SET_GLOBAL_TEMPLATES'; payload: DateMenuTemplate[] | null }
  | { type: 'SET_MARKET_TEMPLATES'; payload: DateMenuTemplate[] | null }
  | { type: 'SET_PRIVATE_TEMPLATES'; payload: DateMenuTemplate[] | null }
  | { type: 'TOGGLE_GLOBAL_EXPANDED' }
  | { type: 'TOGGLE_MARKET_EXPANDED' }
  | { type: 'TOGGLE_PRIVATE_EXPANDED' }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_GLOBAL_SEARCH_TERM'; payload: string }
  | { type: 'SET_MARKET_SEARCH_TERM'; payload: string }
  | { type: 'SET_PRIVATE_SEARCH_TERM'; payload: string }
  | { type: 'SET_GLOBAL_CURRENT_PAGE'; payload: number }
  | { type: 'SET_MARKET_CURRENT_PAGE'; payload: number }
  | { type: 'SET_PRIVATE_CURRENT_PAGE'; payload: number }
  | {
      type: 'SET_LAST_UPDATED_SECTION'
      payload: 'global' | 'market' | 'private' | null
    }

// Define the reducer function
function sidebarReducer(
  state: TemplatesSidebarState,
  action: TemplatesSidebarAction,
): TemplatesSidebarState {
  switch (action.type) {
    case 'SET_GLOBAL_TEMPLATES':
      return { ...state, globalTemplates: action.payload }
    case 'SET_MARKET_TEMPLATES':
      return { ...state, marketTemplates: action.payload }
    case 'SET_PRIVATE_TEMPLATES':
      return { ...state, privateTemplates: action.payload }
    case 'TOGGLE_GLOBAL_EXPANDED':
      return { ...state, globalExpanded: !state.globalExpanded }
    case 'TOGGLE_MARKET_EXPANDED':
      return { ...state, marketExpanded: !state.marketExpanded }
    case 'TOGGLE_PRIVATE_EXPANDED':
      return { ...state, privateExpanded: !state.privateExpanded }
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload }
    case 'SET_GLOBAL_SEARCH_TERM':
      return { ...state, globalSearchTerm: action.payload }
    case 'SET_MARKET_SEARCH_TERM':
      return { ...state, marketSearchTerm: action.payload }
    case 'SET_PRIVATE_SEARCH_TERM':
      return { ...state, privateSearchTerm: action.payload }
    case 'SET_GLOBAL_CURRENT_PAGE':
      return { ...state, globalCurrentPage: action.payload }
    case 'SET_MARKET_CURRENT_PAGE':
      return { ...state, marketCurrentPage: action.payload }
    case 'SET_PRIVATE_CURRENT_PAGE':
      return { ...state, privateCurrentPage: action.payload }
    case 'SET_LAST_UPDATED_SECTION':
      return { ...state, lastUpdatedSection: action.payload }
    default:
      return state
  }
}

// Rest of the interfaces
interface TemplatesSidebarProps {
  userId: string
  hqId: number
  onSelectTemplate: (template: DateMenuTemplate) => void
  searchDateMenuTemplates: (
    req: SearchDateMenuTemplatesReq,
  ) => Promise<DateMenuTemplate[] | null>
}

interface TemplateCardProps {
  template: DateMenuTemplate
  onClick: (template: DateMenuTemplate) => void
}

// Define TemplateCard as a separate component
const TemplateCard: React.FC<TemplateCardProps> = ({ template, onClick }) => (
  <TemplateCardContainer onClick={() => onClick(template)}>
    <TemplateCardHeader>{template.name}</TemplateCardHeader>
    <TemplateCardBody>
      <TemplateCardColumnShort>
        <TemplateCardInfo>
          <InfoLabel>Menus:</InfoLabel>
          <InfoValue>{template.templates.length}</InfoValue>
        </TemplateCardInfo>
        <TemplateCardInfo>
          <InfoLabel>Global:</InfoLabel>
          <InfoValue>{template.hqId ? 'No' : 'Yes'}</InfoValue>
        </TemplateCardInfo>
        <TemplateCardInfo>
          <InfoLabel>Private:</InfoLabel>
          <InfoValue>{template.isPrivate ? 'Yes' : 'No'}</InfoValue>
        </TemplateCardInfo>
      </TemplateCardColumnShort>
      <TemplateCardColumnLong>
        <TemplateCardInfo>
          <InfoLabel>Created By:</InfoLabel>
          <InfoValue>{template.createdByEmail}</InfoValue>
        </TemplateCardInfo>
        <TemplateCardInfo>
          <InfoLabel>Updated By:</InfoLabel>
          <InfoValue>{template.updatedByEmail}</InfoValue>
        </TemplateCardInfo>
        <TemplateCardInfo>
          <InfoLabel>Updated At:</InfoLabel>
          <InfoValue>
            {moment(template.updatedAt).format('MM/DD/YYYY HH:mm:ss Z')}
          </InfoValue>
        </TemplateCardInfo>
      </TemplateCardColumnLong>
    </TemplateCardBody>
  </TemplateCardContainer>
)

// Pagination component
interface PaginationControlsProps {
  currentPage: number
  setCurrentPage: (page: number) => void
  isFullPage: boolean
  isLoading: boolean
}

const PaginationControls: React.FC<PaginationControlsProps> = ({
  currentPage,
  setCurrentPage,
  isFullPage,
  isLoading,
}) => {
  return (
    <PaginationContainer>
      {currentPage > 1 && (
        <PaginationButton
          onClick={() => setCurrentPage(currentPage - 1)}
          disabled={currentPage === 1 || isLoading}
        >
          <FaChevronLeft />
        </PaginationButton>
      )}

      <PaginationInfo>Page {currentPage}</PaginationInfo>

      {isFullPage && (
        <PaginationButton
          onClick={() => setCurrentPage(currentPage + 1)}
          disabled={!isFullPage || isLoading}
        >
          <FaChevronRight />
        </PaginationButton>
      )}
    </PaginationContainer>
  )
}

// Convert to forwardRef
const TemplatesSidebar = forwardRef<TemplatesListRef, TemplatesSidebarProps>(
  ({ userId, hqId, onSelectTemplate, searchDateMenuTemplates }, ref) => {
    // Initialize state with useReducer
    const initialState: TemplatesSidebarState = {
      // Templates state
      globalTemplates: null,
      marketTemplates: null,
      privateTemplates: null,

      // Section expansion state
      globalExpanded: true,
      marketExpanded: false,
      privateExpanded: false,

      // Loading state
      isLoading: false,

      // Search state
      globalSearchTerm: '',
      marketSearchTerm: '',
      privateSearchTerm: '',

      // Pagination state
      globalCurrentPage: 1,
      marketCurrentPage: 1,
      privateCurrentPage: 1,

      // Last updated section
      lastUpdatedSection: null,
    }

    const [state, dispatch] = useReducer(sidebarReducer, initialState)

    // Extract state variables
    const {
      globalTemplates,
      marketTemplates,
      privateTemplates,
      globalExpanded,
      marketExpanded,
      privateExpanded,
      isLoading,
      globalSearchTerm,
      marketSearchTerm,
      privateSearchTerm,
      globalCurrentPage,
      marketCurrentPage,
      privateCurrentPage,
    } = state

    // Throttle timers
    const globalSearchTimerRef = useRef<NodeJS.Timeout | null>(null)
    const marketSearchTimerRef = useRef<NodeJS.Timeout | null>(null)
    const privateSearchTimerRef = useRef<NodeJS.Timeout | null>(null)

    // Load templates for each section when expanded
    const loadGlobalTemplates = useCallback(
      async (currentPage: number, searchTerm: string) => {
        dispatch({ type: 'SET_LOADING', payload: true })
        dispatch({ type: 'SET_GLOBAL_CURRENT_PAGE', payload: currentPage })
        dispatch({ type: 'SET_GLOBAL_SEARCH_TERM', payload: searchTerm })
        const params: SearchDateMenuTemplatesReq = {
          isPrivate: false,
          allHqs: true,
          offset: (currentPage - 1) * RESULTS_PER_PAGE,
          limit: RESULTS_PER_PAGE,
          ...(searchTerm ? { nameSearch: searchTerm } : {}),
        }
        const templates = await searchDateMenuTemplates(params)
        if (templates) {
          dispatch({ type: 'SET_GLOBAL_TEMPLATES', payload: templates })
        }
        dispatch({ type: 'SET_LOADING', payload: false })
      },
      [searchDateMenuTemplates],
    )

    const loadMarketTemplates = useCallback(
      async (currentPage: number, searchTerm: string) => {
        dispatch({ type: 'SET_LOADING', payload: true })
        dispatch({ type: 'SET_MARKET_CURRENT_PAGE', payload: currentPage })
        dispatch({ type: 'SET_MARKET_SEARCH_TERM', payload: searchTerm })
        const params: SearchDateMenuTemplatesReq = {
          isPrivate: false,
          hqId,
          offset: (currentPage - 1) * RESULTS_PER_PAGE,
          limit: RESULTS_PER_PAGE,
          ...(searchTerm ? { nameSearch: searchTerm } : {}),
        }
        const templates = await searchDateMenuTemplates(params)
        if (templates) {
          dispatch({ type: 'SET_MARKET_TEMPLATES', payload: templates })
        }
        dispatch({ type: 'SET_LOADING', payload: false })
      },
      [searchDateMenuTemplates, hqId],
    )

    const loadPrivateTemplates = useCallback(
      async (currentPage: number, searchTerm: string) => {
        dispatch({ type: 'SET_LOADING', payload: true })
        dispatch({ type: 'SET_PRIVATE_CURRENT_PAGE', payload: currentPage })
        dispatch({ type: 'SET_PRIVATE_SEARCH_TERM', payload: searchTerm })
        const params: SearchDateMenuTemplatesReq = {
          isPrivate: true,
          createdByUserId: userId,
          offset: (currentPage - 1) * RESULTS_PER_PAGE,
          limit: RESULTS_PER_PAGE,
          ...(searchTerm ? { nameSearch: searchTerm } : {}),
        }
        const templates = await searchDateMenuTemplates(params)
        if (templates) {
          dispatch({ type: 'SET_PRIVATE_TEMPLATES', payload: templates })
        }
        dispatch({ type: 'SET_LOADING', payload: false })
      },
      [searchDateMenuTemplates, userId],
    )

    // Expose methods via ref for parent component to call
    useImperativeHandle(ref, () => ({
      refreshGlobalTemplates: async () => {
        if (!globalExpanded) {
          dispatch({ type: 'TOGGLE_GLOBAL_EXPANDED' })
        }
        await loadGlobalTemplates(globalCurrentPage, globalSearchTerm)
      },
      refreshMarketTemplates: async () => {
        if (!marketExpanded) {
          dispatch({ type: 'TOGGLE_MARKET_EXPANDED' })
        }
        await loadMarketTemplates(marketCurrentPage, marketSearchTerm)
      },
      refreshPrivateTemplates: async () => {
        if (!privateExpanded) {
          dispatch({ type: 'TOGGLE_PRIVATE_EXPANDED' })
        }
        await loadPrivateTemplates(privateCurrentPage, privateSearchTerm)
      },
    }))

    // Throttled input handlers
    const handleSearchInput =
      (
        searchTermActionType:
          | 'SET_GLOBAL_SEARCH_TERM'
          | 'SET_MARKET_SEARCH_TERM'
          | 'SET_PRIVATE_SEARCH_TERM',
        searchTimerRef: React.MutableRefObject<NodeJS.Timeout | null>,
        loadTemplates: (
          currentPage: number,
          searchTerm: string,
        ) => Promise<void>,
      ) =>
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = e.target
        dispatch({ type: searchTermActionType, payload: value })

        if (searchTimerRef.current) {
          clearTimeout(searchTimerRef.current)
        }

        searchTimerRef.current = setTimeout(() => {
          loadTemplates(1, value)
        }, 1200)
      }

    const handleGlobalSearchInput = handleSearchInput(
      'SET_GLOBAL_SEARCH_TERM',
      globalSearchTimerRef,
      loadGlobalTemplates,
    )
    const handleMarketSearchInput = handleSearchInput(
      'SET_MARKET_SEARCH_TERM',
      marketSearchTimerRef,
      loadMarketTemplates,
    )
    const handlePrivateSearchInput = handleSearchInput(
      'SET_PRIVATE_SEARCH_TERM',
      privateSearchTimerRef,
      loadPrivateTemplates,
    )

    // Initial load and section management
    useEffect(() => {
      if (globalExpanded && !globalTemplates) {
        loadGlobalTemplates(1, globalSearchTerm)
      }
    }, [globalExpanded, loadGlobalTemplates, globalTemplates, globalSearchTerm])

    useEffect(() => {
      if (marketExpanded && !marketTemplates) {
        loadMarketTemplates(1, marketSearchTerm)
      }
    }, [marketExpanded, loadMarketTemplates, marketTemplates, marketSearchTerm])

    useEffect(() => {
      if (privateExpanded && !privateTemplates) {
        loadPrivateTemplates(1, privateSearchTerm)
      }
    }, [
      privateExpanded,
      loadPrivateTemplates,
      privateTemplates,
      privateSearchTerm,
    ])

    return (
      <SidebarContainer>
        <SidebarHeader>Date Menu Templates</SidebarHeader>

        {/* Global Section */}
        <SectionHeader
          onClick={() => dispatch({ type: 'TOGGLE_GLOBAL_EXPANDED' })}
        >
          {globalExpanded ? <FaChevronDown /> : <FaChevronRight />}
          Global Templates
        </SectionHeader>

        {globalExpanded && (
          <>
            <SearchContainer>
              <SearchInput
                type="text"
                value={globalSearchTerm}
                onChange={handleGlobalSearchInput}
                placeholder="Search global templates..."
              />
              <SearchButton
                onClick={() =>
                  loadGlobalTemplates(globalCurrentPage, globalSearchTerm)
                }
                title="Search now"
              >
                <FaSearch />
              </SearchButton>
            </SearchContainer>

            {isLoading && <LoadingText>Loading templates...</LoadingText>}

            {!isLoading && globalTemplates && globalTemplates.length === 0 && (
              <EmptyStateText>No global templates found</EmptyStateText>
            )}

            {globalTemplates &&
              globalTemplates.map((template) => (
                <TemplateCard
                  key={template.id}
                  template={template}
                  onClick={onSelectTemplate}
                />
              ))}

            {globalTemplates && (
              <PaginationControls
                currentPage={globalCurrentPage}
                setCurrentPage={(pageNumber) =>
                  loadGlobalTemplates(pageNumber, globalSearchTerm)
                }
                isFullPage={globalTemplates.length === RESULTS_PER_PAGE}
                isLoading={isLoading}
              />
            )}

            <YSpacing height="10px" />
          </>
        )}

        {/* Market Section */}
        <SectionHeader
          onClick={() => dispatch({ type: 'TOGGLE_MARKET_EXPANDED' })}
        >
          {marketExpanded ? <FaChevronDown /> : <FaChevronRight />}
          Market Templates
        </SectionHeader>

        {marketExpanded && (
          <>
            <SearchContainer>
              <SearchInput
                type="text"
                value={marketSearchTerm}
                onChange={handleMarketSearchInput}
                placeholder="Search market templates..."
              />
              <SearchButton
                onClick={() =>
                  loadMarketTemplates(marketCurrentPage, marketSearchTerm)
                }
                title="Search now"
              >
                <FaSearch />
              </SearchButton>
            </SearchContainer>

            {isLoading && <LoadingText>Loading templates...</LoadingText>}

            {!isLoading && marketTemplates && marketTemplates.length === 0 && (
              <EmptyStateText>No market templates found</EmptyStateText>
            )}

            {marketTemplates &&
              marketTemplates.map((template) => (
                <TemplateCard
                  key={template.id}
                  template={template}
                  onClick={onSelectTemplate}
                />
              ))}

            {marketTemplates && (
              <PaginationControls
                currentPage={marketCurrentPage}
                setCurrentPage={(pageNumber) =>
                  loadMarketTemplates(pageNumber, marketSearchTerm)
                }
                isFullPage={marketTemplates.length === RESULTS_PER_PAGE}
                isLoading={isLoading}
              />
            )}

            <YSpacing height="10px" />
          </>
        )}

        {/* Private Section */}
        <SectionHeader
          onClick={() => dispatch({ type: 'TOGGLE_PRIVATE_EXPANDED' })}
        >
          {privateExpanded ? <FaChevronDown /> : <FaChevronRight />}
          Private Templates
        </SectionHeader>

        {privateExpanded && (
          <>
            <SearchContainer>
              <SearchInput
                type="text"
                value={privateSearchTerm}
                onChange={handlePrivateSearchInput}
                placeholder="Search private templates..."
              />
              <SearchButton
                onClick={() =>
                  loadPrivateTemplates(privateCurrentPage, privateSearchTerm)
                }
                title="Search now"
              >
                <FaSearch />
              </SearchButton>
            </SearchContainer>

            {isLoading && <LoadingText>Loading templates...</LoadingText>}

            {!isLoading &&
              privateTemplates &&
              privateTemplates.length === 0 && (
                <EmptyStateText>No private templates found</EmptyStateText>
              )}

            {privateTemplates &&
              privateTemplates.map((template) => (
                <TemplateCard
                  key={template.id}
                  template={template}
                  onClick={onSelectTemplate}
                />
              ))}

            {privateTemplates && (
              <PaginationControls
                currentPage={privateCurrentPage}
                setCurrentPage={(pageNumber) =>
                  loadPrivateTemplates(pageNumber, privateSearchTerm)
                }
                isFullPage={privateTemplates.length === RESULTS_PER_PAGE}
                isLoading={isLoading}
              />
            )}

            <YSpacing height="10px" />
          </>
        )}
      </SidebarContainer>
    )
  },
)

// Styled components
const SidebarContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 550px;
  border: 1px solid ${colors.gray200};
  border-radius: 8px;
  overflow-y: auto;
  max-height: calc(100vh - 200px);
  background-color: white;
`

const SidebarHeader = styled.div`
  background-color: ${colors.gray100};
  padding: 15px;
  font-weight: bold;
  font-size: 16px;
  border-bottom: 1px solid ${colors.gray200};
`

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 15px;
  font-weight: bold;
  background-color: ${colors.gray200};
  border-bottom: 1px solid ${colors.gray300};
  cursor: pointer;
`

const TemplateCardContainer = styled.div`
  border-bottom: 1px solid ${colors.gray200};
  padding: 10px 15px;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: ${colors.blue100};
  }
`

const TemplateCardHeader = styled.div`
  font-weight: bold;
  font-size: 14px;
  margin-bottom: 8px;
  text-decoration: underline;
`

const TemplateCardBody = styled.div`
  font-size: 12px;
  display: flex;
  gap: 10px;
`

const TemplateCardColumnShort = styled.div`
  flex: 1;
`

const TemplateCardColumnLong = styled.div`
  flex: 2;
`

const TemplateCardInfo = styled.div`
  display: flex;
  margin-bottom: 4px;
`

const InfoLabel = styled.div`
  color: ${colors.gray400};
  width: 80px;
`

const InfoValue = styled.div`
  flex: 1;
  word-break: break-word;
`

const LoadingText = styled.div`
  padding: 15px;
  color: ${colors.gray400};
  font-style: italic;
`

const EmptyStateText = styled.div`
  padding: 15px;
  color: ${colors.gray400};
  font-style: italic;
`

// Search components styling
const SearchContainer = styled.div`
  display: flex;
  padding: 10px 15px;
  border-bottom: 1px solid ${colors.gray200};
`

const SearchInput = styled.input`
  flex: 1;
  border: 1px solid ${colors.gray200};
  border-radius: 4px 0 0 4px;
  padding: 8px 12px;
  font-size: 14px;
  outline: none;

  &:focus {
    border-color: ${colors.blue400};
  }
`

const SearchButton = styled.button`
  background-color: ${colors.blue400};
  color: white;
  border: none;
  border-radius: 0 4px 4px 0;
  padding: 0 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background-color: ${colors.blue400};
  }
`

// Pagination components styling
const PaginationContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 10px 15px;
  gap: 10px;
  border-bottom: 1px solid ${colors.gray200};
`

const PaginationButton = styled.button`
  background-color: ${colors.blue400};
  color: white;
  border: none;
  border-radius: 4px;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  &:hover:not(:disabled) {
    background-color: ${colors.blue300};
  }

  &:disabled {
    background-color: ${colors.gray300};
    cursor: not-allowed;
  }
`

const PaginationInfo = styled.div`
  font-size: 14px;
  color: ${colors.gray400};
`

export default TemplatesSidebar
